import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "@/components/lib/utils";

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { snaplandId?: string }
>(({ className, children, snaplandId, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-[hsl(var(--color-muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--color-ring))] disabled:cursor-not-allowed disabled:opacity-50",
      "border-[hsl(var(--color-input))]",
      "rtl:flex-row-reverse rtl:text-right",
      className
    )}
    data-snapland-id={snaplandId || "select-trigger"}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <svg viewBox="0 0 24 24" className="h-4 w-4 opacity-60" aria-hidden data-snapland-id={`${snaplandId || "select"}-icon`}>
        <path d="M7 10l5 5 5-5z" fill="currentColor" />
      </svg>
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & { snaplandId?: string }
>(({ className, children, position = "popper", snaplandId, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-[hsl(var(--color-popover))] text-[hsl(var(--color-popover-foreground))] shadow-md border-[hsl(var(--color-border))]",
        className
      )}
      position={position}
      data-snapland-id={snaplandId || "select-content"}
      {...props}
    >
      <SelectPrimitive.Viewport className="p-1" data-snapland-id={`${snaplandId || "select"}-viewport`}>
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label> & { snaplandId?: string }
>(({ className, snaplandId, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold rtl:text-right rtl:[direction:rtl]", className)}
    data-snapland-id={snaplandId || "select-label"}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { snaplandId?: string }
>(({ className, children, snaplandId, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-[hsl(var(--color-accent))] focus:text-[hsl(var(--color-accent-foreground))] rtl:flex-row-reverse rtl:text-right",
      className
    )}
    data-snapland-id={snaplandId || "select-item"}
    {...props}
  >
    <SelectPrimitive.ItemText data-snapland-id={`${snaplandId || "select"}-item-text`}>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="ms-2 inline-flex items-center justify-center" data-snapland-id={`${snaplandId || "select"}-item-indicator`}>
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden data-snapland-id={`${snaplandId || "select"}-item-check-icon`}>
        <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
};

